import {AfterViewInit, Component, effect, ElementRef, input, OnInit, output, signal, viewChild} from '@angular/core';
import {BrowserService} from '@core/service/browser.service';
import {WGS84DecimalPoint} from '@domain/place/geo.interface';
import {PointCoords} from '@domain/common/common';
import {GeoUtil} from '@domain/common/util/geo.util';
import {MarkerOptions} from 'leaflet';

@Component({
  selector: 'app-map',
  imports: [],
  styleUrl: './map.component.scss',
  templateUrl: './map.component.html'
})
export class MapComponent implements AfterViewInit, OnInit {

  public readonly mapContainer = viewChild.required<ElementRef<HTMLDivElement>>('mapContainer');
  public readonly center = input<WGS84DecimalPoint | PointCoords | null>(null);
  public readonly coords = input<WGS84DecimalPoint | PointCoords | null>(null);
  public readonly zoom = input<number>(15);
  public readonly editable = input(false);
  public readonly isPositioning = signal(false);

  public readonly coordinates = signal<WGS84DecimalPoint | null>(null);
  public readonly updated = output<WGS84DecimalPoint>();

  private map!: L.Map;
  private marker: L.Marker | null = null;
  private locateControl: L.Control | null = null;
  private locateButton: HTMLButtonElement | null = null;

  private leaflet: any;

  constructor(private readonly browserService: BrowserService) {
    if (this.browserService.isBrowser) {
      effect(() => {
        this.coordinates.set(GeoUtil.toDecimal(this.coords()));
        this.refreshMap();
      });
    }
  }

  public ngOnInit() {
    this.coordinates.set(GeoUtil.toDecimal(this.coords()));
  }

  public async ngAfterViewInit() {
    if (!this.browserService.isBrowser) {
      return;
    }
    const container = this.mapContainer().nativeElement;
    const module = await import('leaflet');
    const L: typeof module = this.normalizeLeafletModule(module);

    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'img/leaflet/marker-icon-2x.png',
      iconUrl: 'img/leaflet/marker-icon.png',
      shadowUrl: 'img/leaflet/marker-shadow.png',
    });

    this.leaflet = L;
    this.map = L.map(container, {
      zoom: this.zoom(),
      scrollWheelZoom: false,
    });

    L.tileLayer(
      'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      {
        maxZoom: 19,
        attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      }
    ).addTo(this.map);
    this.initLocateControl();
    this.refreshMap();
  }

  private normalizeLeafletModule(module: any) {
    return module.default ?? module;
  }

  private refreshMap(): void {
    if (this.map) {
      const coords = this.coordinates();
      if (coords) {
        this.createMarker(coords);
      } else if (this.editable()) {
        this.map.getContainer().style.cursor = 'crosshair';
        this.map.on('click', (e: L.LeafletMouseEvent) => {
          const { lat, lng } = e.latlng;
          this.createMarker({ latitude: lat, longitude: lng });
          this.map.getContainer().style.cursor = 'grab';
        });
      }
      this.centerMap();
    }
  }

  private createMarker(coords: WGS84DecimalPoint): void {
    if (!coords) {
      return;
    }
    if (this.marker) {
      this.marker.setLatLng([coords.latitude, coords.longitude]);
    } else {
      const markerOptions: MarkerOptions = {};
      if (this.editable()) {
        markerOptions.draggable = true;
      }
      this.marker = this.leaflet.marker([coords.latitude, coords.longitude], markerOptions);
      this.marker?.addTo(this.map);
    }
    if (this.editable()) {
      this.marker?.on('dragend', (e) => {
        const marker = e.target;
        const position = marker.getLatLng();
        this.updateMarker(position.lat, position.lng);
      });
    }
  }

  private initLocateControl(): void {
    if (!this.editable()) {
      return;
    }
    const self = this;
    const PointerControlClass = self.leaflet.Control.extend({
      options: {
        position: 'topleft' // Places it right below the zoom buttons
      },

      onAdd: function() {
        // Create a standard Leaflet bar container wrapper
        const container = self.leaflet.DomUtil.create('div', 'leaflet-bar');
        const button = self.leaflet.DomUtil.create('button', 'map-tool-btn', container) as HTMLButtonElement;
        button.innerHTML = '⌖'; // Default icon for move mode
        button.title = 'Toggle Add Marker Mode';

        button.style.width = '30px';
        button.style.height = '30px';
        button.style.lineHeight = '30px';
        button.style.borderRadius = '2px';
        button.style.fontSize = '28px';
        button.style.color = 'blue';
        button.style.borderStyle = 'none';
        button.style.backgroundColor = 'white';
        self.locateButton = button;
        self.leaflet.DomEvent.disableClickPropagation(container);
        self.leaflet.DomEvent.on(button, 'click', () => {
          self.toggleLocateMode();
        });

        return container;
      }
    });
    this.locateControl = new PointerControlClass();
    this.map.addControl(this.locateControl!);
  }

  toggleLocateMode(): void {
    this.isPositioning.update(p => !p);
    if (!this.locateButton) {
      return;
    }
    if (this.isPositioning()) {
      this.locateButton.innerHTML = '⍉';
      this.locateButton.style.fontSize = '22px';
      this.locateButton.style.color = 'red';
      this.map.getContainer().style.cursor = 'crosshair';
      this.map.on('click', this.handleMapClick);
    } else {
      this.locateButton.innerHTML = '⌖';
      this.locateButton.style.fontSize = '28px';
      this.locateButton.style.color = 'blue';
      this.map.getContainer().style.cursor = 'grabbing';
      this.map.off('click', this.handleMapClick);
    }
  }

  private handleMapClick = (e: L.LeafletMouseEvent): void => {
    const { lat, lng } = e.latlng;
    this.updateMarker(lat, lng);
  };

  private updateMarker(lat: number, lng: number): void {
    const newsCoords = { latitude: lat, longitude: lng };
    this.coordinates.set(newsCoords);
    this.createMarker(newsCoords);
    this.centerMap();
    this.updated.emit(newsCoords);
  }

  private centerMap() {
    // Create a boundary based on the markers
    const center = this.coordinates() ?? this.center();
    if (!center) {
      return;
    }
    const wgs84 = GeoUtil.toDecimal(center);
    if (!wgs84) {
      return;
    }
    const latLng = new this.leaflet.LatLng(wgs84.latitude, wgs84.longitude);
    const bounds = this.leaflet.latLngBounds([latLng]);

    // Fit the map into the boundary
    this.map.fitBounds(bounds, {
      maxZoom: this.zoom()
    });
  }
}
